import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AWSChallenge } from 'common/constant/authentication/aws-challenge';
import { BehaviorSubject, catchError, combineLatest, filter, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { EdgeDeviceService } from 'services/edge-device/edge-device.service';
import { StorageService } from 'services/storage/storage.service';
import { UserService } from 'services/user/user.service';
import { User } from 'src/app/common/class/user';
import { RoutePath } from 'src/app/common/constant/route-path';
import { Logger } from 'src/app/common/utility/logger/logger';
import { Strings } from 'src/app/common/utility/string/string';
import { Environment } from 'src/environments/environment';
import { ConnectionState } from '../socket/classes/connection-state';
import { SocketService } from '../socket/socket.service';
import { TokenService } from '../token/token.service';
import { CognitoService } from './cognito/cognito.service';
import { ChangePasswordResult } from './model/change-password-result';
import { LoginResult } from './model/login-result';

export enum AuthenticationState {
  Authenticated = 0,
  NotAuthenticated = 1
}

/**
 * Authenticating Users against an AWS Cognito Pool and AWS web socket 
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
  private unsubscribe: Subject<void> = new Subject();
  private loginChanged: Subject<boolean> = new Subject<boolean>();
  private userChanged$: BehaviorSubject<User> = new BehaviorSubject<User>(new User());
  private authenticationState: BehaviorSubject<AuthenticationState> = new BehaviorSubject<AuthenticationState>(AuthenticationState.NotAuthenticated);

  constructor(
    private tokenService: TokenService,
    private socketService: SocketService,
    private cognitoService: CognitoService,
    private router: Router,
    private edgeDeviceService: EdgeDeviceService,
    private userService: UserService
  ) {
    this.refreshUser();
    this.monitorLogin();
    this.loginChanged.next(this.loggedIn);
  }

  public get listenForAuthenticationState(): BehaviorSubject<AuthenticationState> {
    return this.authenticationState;
  }

  private set User(value: User) {
    this.userChanged$.next(value);
  }

  public get loggedIn(): boolean {
    return this.tokenService.valid
  }

  public login(userName: string, password: string, companyId: string): Observable<LoginResult> {
    this.logout(false)
    return this.cognitoService.signIn(userName, password, companyId)
      .pipe(
        catchError(error => {
          const message: string = error?.errorMessage ?? error?.message ?? error?.code ?? error?.stack ?? 'Unspecified'

          Logger.debug(`Cognito Login Error: '${message}'`)
          return of(new LoginResult(false, Strings.Empty, error?.errorMessage));
        }),
        take(1),
        switchMap(result => {

          let returnValue: LoginResult = this.createLoginResult({})

          if (result.challengeName === AWSChallenge.NewPasswordRequired) {
            Logger.debug(`Password Change required for user ${userName}`)
            returnValue.CognitoUser = result
          } else {
            if (!(result instanceof LoginResult)) {
              returnValue = this.createLoginResult(result)
              this.loginChanged.next(returnValue.loggedIn)
            }
          }
          this.User = StorageService.loggedOnUser
          return of(returnValue)
        })
      )
  }

  public logout(redirect: boolean = true): void {
    this.User = new User();
    StorageService.PurgeVolatile()
    StorageService.PurgeCognito()
    this.loginChanged.next(this.loggedIn)
    this.socketService.disconnect();
    if (redirect) {
      this.router.navigate([RoutePath.Login])
    }
  }

  public getUserProfile(): Observable<User> {
    return this.userChanged$;
  }

  public changeFirstPassword(user: any, newPassword: string): Observable<LoginResult> {
    return this.cognitoService.changeFirstPassword(user, newPassword).pipe(
      catchError(error => {
        const message: string = error?.errorMessage ?? error?.message ?? error?.code ?? error?.stack ?? 'Unspecified'

        Logger.debug(`Cognito Login Error: '${message}'`)
        return of(new LoginResult(false, Strings.Empty, Strings.Empty, error?.code ?? error?.message));
      }),
      take(1),
      switchMap(result => {
        if (result instanceof LoginResult) {
          return of(result)
        }

        let returnValue: LoginResult = ChangePasswordResult.createLoginResult(result)
        // this.saveLoginData(returnValue)
        return of(returnValue)
      })
    )
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private refreshUser(): void {
    //TODO: AWS Create password
    this.User = new User();
  }

  private createLoginResult(loginResult: any): LoginResult {
    return new LoginResult(
      !!loginResult?.attributes?.email,
      loginResult?.username,
      loginResult?.attributes?.email,
      Strings.Empty,
      loginResult?.signInUserSession?.accessToken?.jwtToken,
      loginResult?.signInUserSession?.idToken?.jwtToken,
      loginResult?.signInUserSession?.refreshToken?.token,
      loginResult
    )
  }

  private monitorLogin(): void {
    this.loginChanged.pipe(takeUntil(this.unsubscribe)).subscribe(loggedIn => {
      if (loggedIn) {
        this.authenticationState.next(AuthenticationState.Authenticated);
        this.preloadServices();
      } else {
        this.authenticationState.next(AuthenticationState.NotAuthenticated);
      }
    });

    combineLatest([this.loginChanged, this.socketService.ConnectionState])
      .pipe(
        takeUntil(this.unsubscribe),
        filter(([loggedIn, connected]) => loggedIn && connected === ConnectionState.Disconnected),
        switchMap(_state => {
          let returnValue;
          if (this.socketService.FailedConnectionAttempts >= Environment.SocketRetries) {
            Logger.warning(`Failed to connect to websocket service at: ${Environment.SocketUrl} after ${Environment.SocketRetries} retry attempts`)
            returnValue = of(ConnectionState.Disconnected)

          } else {
            returnValue = this.connect()
          }
          return returnValue
        }),
      )
      .subscribe((state: ConnectionState) => {
        let message: string = Strings.Empty;
        switch (state) {
          case ConnectionState.Connected:
            message = "Socket Connection established";
            break;
          case ConnectionState.Disconnected:
            message = "Socket Disconnected";
            break;
          case ConnectionState.Connecting:
            message = "Socket Connecting";
            break;
          case ConnectionState.Disconnecting:
            message = "Socket Disconnecting";
            break;
          case ConnectionState.Error:
            message = "Socket Errored";
            break;
        }
        Logger.debug(message)
      });
  }

  private connect(): Observable<ConnectionState> {
    this.socketService.connect(Environment.SocketUrl, this.tokenService.AccessToken || Strings.Empty, this.cognitoService.PoolId)
    return this.socketService.ConnectionState
  }

  private preloadServices(): void {
    Logger.debug("loading")
    this.edgeDeviceService.refreshEdgeDevices()
    this.userService.refreshUsers().subscribe()
  }
}
